import { ApiError } from '@api/api-client';
import { login as loginRequest } from '@api/auth-api';
import FieldError from '@components/field-error';
import { zodResolver } from '@hookform/resolvers/zod';
import { useAuth } from '@hooks/use-auth';
import { type LoginFormData, loginSchema } from '@validation/schemas';
import { Award } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router-dom';
import { sileo } from 'sileo';

export default function Login(): React.JSX.Element {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [isLoading, setIsLoading] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '' },
  });

  const onSubmit = async (data: LoginFormData): Promise<void> => {
    setIsLoading(true);

    try {
      const userInfo = await loginRequest({ email: data.email, password: data.password });
      login(userInfo);
      // Signing in with a password an admin reset lands on the change screen, not
      // on the app: the password is temporary and its owner did not choose it.
      navigate(userInfo.mustChangePassword ? '/change-password' : '/editions', { replace: true });
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) {
        setError('password', { message: 'Invalid email or password' });
      } else {
        sileo.error({ title: 'An error occurred. Please try again.' });
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-card">
        <div className="login-card__accent" />
        <div className="login-card__body">
          <div className="login-card__header">
            <div className="login-card__logo">
              <Award size={28} />
            </div>
            <h1 className="login-card__title">DP Awards</h1>
            <p className="login-card__description">Access the awards management</p>
          </div>

          <form className="login-form" onSubmit={(e) => void handleSubmit(onSubmit)(e)}>
            <div className="form-group">
              <label htmlFor="email" className="form-label">
                Email
              </label>
              <input
                id="email"
                type="email"
                className={`form-input${errors.email ? ' form-input--error' : ''}`}
                placeholder="user@example.com"
                autoComplete="email"
                {...register('email')}
              />
              <FieldError error={errors.email} />
            </div>

            <div className="form-group">
              <label htmlFor="password" className="form-label">
                Password
              </label>
              <input
                id="password"
                type="password"
                className={`form-input${errors.password ? ' form-input--error' : ''}`}
                placeholder="••••••••"
                autoComplete="current-password"
                {...register('password')}
              />
              <FieldError error={errors.password} />
            </div>

            <button type="submit" className="btn btn-primary" disabled={isLoading}>
              {isLoading ? 'Signing in...' : 'Sign in'}
            </button>
          </form>

          <div style={{ textAlign: 'center', marginTop: 'var(--space-5)' }}>
            <Link to="/create-account" className="btn btn-ghost btn-sm">
              Create account
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
